import React, { useEffect, useRef, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { activeToggle } from "../utils/helper";
import ProfilePersonalDetails from "../pages/hcp/UserProfile/ProfilePersonalDetails";
import ProfileChangePassword from "../pages/hcp/UserProfile/ProfileChangePassword";
import { useUserProfile } from "../hooks/getUserProfile";
import FooterButton from "../components/footerButton";
import { toast } from "react-toastify";
import Logo from "../assets/images/normal_logo.png";
import { BiChevronDown } from "react-icons/bi";
import { useAuth } from "../services/authProvider";
import { logoutWithWarning } from "../utils/logoutWithWarning";

const FirstLoginProfileLayout = (props) => {
  const { data: userProfile, isLoading, refetch } = useUserProfile();
  const { logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".dropdown")) {
        setDropdownOpen(false);
      }
    };
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && dropdownOpen) setDropdownOpen(false);
    };
    if (dropdownOpen) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [dropdownOpen]);

  const markProfileComplete = (userId) => {
    if (userId) localStorage.setItem(`profileComplete_${userId}`, "true");
  };

  const handleSaveProfile = async () => {
    // Refetch to validate against actual backend state
    const result = await refetch();
    const profile = result?.data?.data;

    if (!profile?.sex) {
      toast.error("Biological Sex is required.");
      return;
    }
    if (!profile?.dateOfBirth) {
      toast.error("Date of Birth is required.");
      return;
    }
    if (!profile?.languagePreference) {
      toast.error("Spoken Language Preference is required.");
      return;
    }
    if (!profile?.emails?.length && !profile?.email) {
      toast.error("At least one Email Address is required.");
      return;
    }
    if (!profile?.phones?.length) {
      toast.error("At least one Phone Number is required.");
      return;
    }
    if (!profile?.mailingAddresses || profile.mailingAddresses.length === 0) {
      toast.error("A Mailing Address is required.");
      return;
    }

    markProfileComplete(profile.id);
    toast.success("Profile saved successfully!");
    props.history.push("/hcp/patient/list");
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-white bg-white shadow-sm pr-4 py-3">
        <Link to="/hcp/first_login_profile/personal_details" className="navbar-brand ml-3">
          <img src={Logo} width={119} height={60} alt="POMMe Health" className="img-fluid" />
        </Link>
        <div className="navbar-nav ml-auto d-flex align-items-center">
          <ul className="navbar-nav">
            <li className={`nav-item dropdown ${dropdownOpen ? "show" : ""}`}>
              <a
                className="navbar-btn px-2 rounded d-flex align-items-center justify-content-center text_small text-decoration-none"
                href="#"
                id="firstLoginDropdown"
                role="button"
                aria-expanded={dropdownOpen}
                onClick={toggleDropdown}
              >
                <div className="d-flex flex-column align-items-start mr-2">
                  <span>
                    {userProfile?.data?.lastName}, {userProfile?.data?.firstName}
                  </span>
                  <span>
                    {userProfile?.data?.careProvider
                      ? userProfile?.data?.careProvider?.position?.abbreviation
                      : ""}
                  </span>
                </div>
                <BiChevronDown size={30} className="navbar-btn-icon" />
              </a>
              <div
                className={`dropdown-menu w-small dropdown-menu-right pl-0 ml-0 ${
                  dropdownOpen ? "show" : ""
                }`}
                aria-labelledby="firstLoginDropdown"
              >
                <div
                  className="dropdown-item ml-0 pl-3 text_small cursor-pointer"
                  onClick={() => {
                    setDropdownOpen(false);
                    logoutWithWarning(logout);
                  }}
                >
                  <i className="fa fa-sign-out" /> &nbsp;Logout
                </div>
              </div>
            </li>
          </ul>
        </div>
      </nav>

      {/* Content */}
      <div className="flex-grow-1 overflow-hidden" style={{ backgroundColor: "#f4f4f4" }}>
        <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
          <div className="card border-0 h-100">
            <div className="card-body mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="font-weight-lighter mb-0">User Profile</h5>
                <label className="small-text decoration-0">
                  POMMe ID:{" "}
                  <span className="ml-1">{userProfile?.data?.pommeId}</span>
                </label>
              </div>
              <hr />
              <div className="d-flex my-12">
                <Link
                  to={"/hcp/first_login_profile/personal_details"}
                  className={`btn btn-user-profile mr-2 ${
                    activeToggle("/hcp/first_login_profile/personal_details")
                      ? "btn-user-profile-active"
                      : ""
                  }`}
                >
                  Personal Details
                </Link>
                <Link
                  to={"/hcp/first_login_profile/change_password"}
                  className={`btn btn-user-profile mr-2 ${
                    activeToggle("/hcp/first_login_profile/change_password")
                      ? "btn-user-profile-active"
                      : ""
                  }`}
                >
                  Change Password
                </Link>
              </div>
              <hr />
              <div className="noScroll h-90 px-35">
                <Switch>
                  <Route
                    path="/hcp/first_login_profile/personal_details"
                    component={ProfilePersonalDetails}
                  />
                  <Route
                    path="/hcp/first_login_profile/change_password"
                    component={ProfileChangePassword}
                  />
                  <Redirect
                    from="/hcp/first_login_profile"
                    to="/hcp/first_login_profile/personal_details"
                  />
                </Switch>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <FooterButton
        child1={<div />}
        child2={
          <button
            className="btn btn_outline_light px-5"
            onClick={handleSaveProfile}
            disabled={isLoading}
          >
            <small>SAVE PROFILE</small>
          </button>
        }
      />
    </div>
  );
};

export default FirstLoginProfileLayout;
